'use client';

import { c as _c } from "react/compiler-runtime";
import type { DateRange } from '@repo/ui/components';
import { collapseSameDayRange, lastDayOfMonth, startOfMonth } from '@repo/sdk/date';
import { Button, ButtonGroup, Calendar, Checkbox, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, Input, Popover, PopoverAnchor, PopoverContent, PopoverTrigger, Separator, Slider, Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/components';
import { DateFormatter } from '@repo/ui/date-formatter';
import { ChevronDownIcon, Ellipsis } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { cloneElement, isValidElement, memo, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Column, ColumnDataType, ColumnOption, ColumnOptionExtended, DataTableFilterActions, DateFilterValue, DateWindow, FilterModel, FilterOperators, FilterStrategy, RelativeDate } from '../core';
import { isColumnOfType } from '../core';
import { numberFilterOperators } from '../core/operators';
import { useDebounceCallback } from '../hooks/use-debounce-callback';
import { take } from '../lib/array';
import { debounce } from '../lib/debounce';
import { createNumberRange } from '../lib/helpers';
import { type Locale, t } from '../lib/i18n';
import { dayRangeLabel, findMonthChoice, isAbsoluteDate, isDayOffset, isRelativeDate, isRelativeDateChoice, MAX_DAY_OFFSET, MAX_MONTH_OFFSET, MONTH_CHOICES, monthChoice, monthWindowLabel, overdueChoice, overdueDays, overdueLabel, parseDayOffset, parseRelativeDays, parseWindowCount, readWindow, relativeDateChoices, relativeDateLabel, resolveDateFilterValues, windowAroundToday } from '../lib/relative-date';
import { DebouncedInput } from '../ui/debounced-input';
import { FilterEditorAutoFocus } from './filter-editor-auto-focus';
interface FilterValueProps<TData, TType extends ColumnDataType> {
  actions: DataTableFilterActions;
  column: Column<TData, TType>;
  filter: FilterModel<TType>;
  locale?: Locale;
  strategy: FilterStrategy;
}
export const FilterValue =
// SAFETY: the memo wrapper renders the component with the same props and is only used in JSX;
// memo's typings would drop the TData and TType generics.
memo(__FilterValue) as typeof __FilterValue;
interface FilterValueControllerProps<TData, TType extends ColumnDataType = ColumnDataType> {
  actions: DataTableFilterActions;
  column: Column<TData, TType>;
  /* Absent while the column has no filter yet. */
  filter?: FilterModel<TType>;
  locale?: Locale;
  strategy: FilterStrategy;
}
interface FilterValueDisplayProps<TData, TType extends ColumnDataType = ColumnDataType> {
  actions: DataTableFilterActions;
  column: Column<TData, TType>;
  filter: FilterModel<TType>;
  locale?: Locale;
}
export function FilterValueAmountDisplay(t0) {
  const $ = _c(13);
  const {
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  if (!filter || !filter.values || filter.values.length === 0) {
    return null;
  }
  let t2;
  if ($[0] !== filter.values) {
    t2 = filter.values.map(_temp).filter(_temp2);
    $[0] = filter.values;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const amounts = t2;
  if (amounts.length === 0) {
    let t3;
    if ($[2] !== locale) {
      t3 = t("anyAmount", locale);
      $[2] = locale;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t3) {
      t4 = <span>{t3}</span>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  const isRange = numberFilterOperators[filter.operator].target === "multiple";
  let t3;
  if ($[6] !== amounts[0] || $[7] !== amounts[1] || $[8] !== isRange || $[9] !== locale) {
    t3 = isRange ? `${amounts[0]} ${t("and", locale)} ${amounts[1] ?? ""}` : amounts[0];
    $[6] = amounts[0];
    $[7] = amounts[1];
    $[8] = isRange;
    $[9] = locale;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <span className="tracking-tight tabular-nums" data-sentry-component="FilterValueAmountDisplay" data-sentry-source-file="filter-value.tsx">{t3}</span>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function _temp2(amount) {
  return amount !== null;
}
function _temp(value) {
  return value.amount;
}
export function FilterValueCompanyDisplay(t0) {
  const $ = _c(3);
  const {
    filter,
    locale: t1
  } = t0;
  t1 === undefined ? "en" : t1;
  if (!filter || !filter.values || filter.values.length === 0) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Ellipsis className="size-4" />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  const value = filter.values[0];
  let t2;
  if ($[1] !== value.label) {
    t2 = <span data-sentry-component="FilterValueCompanyDisplay" data-sentry-source-file="filter-value.tsx">{value.label}</span>;
    $[1] = value.label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/*
 * The chip's currency segment for an amount filter, chosen apart from the amount: the amounts and
 * operator stay as they are. Any currency on a filter with no amount leaves nothing to filter by,
 * so the filter goes once the menu closes; going at once would drop the open menu and its focus.
 */
export function FilterValueCurrency(t0) {
  const $ = _c(20);
  const {
    actions,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const [isCleared, setIsCleared] = useState(false);
  if (!isColumnOfType(column, "amount") || filter.type !== "amount") {
    return null;
  }
  let t2;
  if ($[0] !== actions || $[1] !== column || $[2] !== filter) {
    t2 = currencies => {
      const clearsFilter = currencies.length === 0 && filter.values.every(_temp3);
      setIsCleared(clearsFilter);
      if (clearsFilter) {
        return;
      }
      actions.setFilterValue(column, filter.values.map(value_0 => ({
        ...value_0,
        currencies
      })));
    };
    $[0] = actions;
    $[1] = column;
    $[2] = filter;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const changeCurrencies = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Separator orientation="vertical" data-sentry-element="Separator" data-sentry-source-file="filter-value.tsx" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== column) {
    t4 = column.getOptions();
    $[5] = column;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== actions || $[8] !== column.id || $[9] !== isCleared) {
    t5 = () => {
      if (isCleared) {
        actions.removeFilter(column.id);
      }
    };
    $[7] = actions;
    $[8] = column.id;
    $[9] = isCleared;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== filter || $[12] !== isCleared) {
    t6 = isCleared ? [] : filter.values[0]?.currencies ?? [];
    $[11] = filter;
    $[12] = isCleared;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== changeCurrencies || $[15] !== locale || $[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t7 = <>{t3}<CurrencySelect className="h-full rounded-none px-2 text-xs" currencies={t4} locale={locale} onChange={changeCurrencies} onClose={t5} value={t6} variant="ghost" data-sentry-element="CurrencySelect" data-sentry-source-file="filter-value.tsx" /></>;
    $[14] = changeCurrencies;
    $[15] = locale;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function _temp3(value) {
  return value.amount === null;
}
export function FilterValueDateDisplay(t0) {
  const $ = _c(28);
  const {
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  if (!filter) {
    return null;
  }
  if (filter.values.length === 0) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Ellipsis className="size-4" />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] !== column || $[2] !== filter || $[3] !== locale) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const pastDue = overdueDays(column, filter);
      if (pastDue !== undefined) {
        t2 = <span>{overdueLabel(pastDue, locale)}</span>;
        break bb0;
      }
    }
    $[1] = column;
    $[2] = filter;
    $[3] = locale;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let relative;
  let t3;
  if ($[5] !== filter.values || $[6] !== locale) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb1: {
      relative = filter.values.every(isRelativeDate) ? filter.values : [];
      const month = findMonthChoice(relative);
      if (month) {
        t3 = <span>{t(month.label, locale)}</span>;
        break bb1;
      }
    }
    $[5] = filter.values;
    $[6] = locale;
    $[7] = relative;
    $[8] = t3;
  } else {
    relative = $[7];
    t3 = $[8];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[9] !== locale || $[10] !== relative) {
    t4 = monthWindowLabel(relative, locale);
    $[9] = locale;
    $[10] = relative;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const monthWindow = t4;
  if (monthWindow) {
    let t5;
    if ($[12] !== monthWindow) {
      t5 = <span>{monthWindow}</span>;
      $[12] = monthWindow;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (filter.values.every(isDayOffset)) {
    let t5;
    if ($[14] !== column.dayCounts || $[15] !== filter.values) {
      t5 = column.dayCounts && !isRelativeDateChoice(filter.values);
      $[14] = column.dayCounts;
      $[15] = filter.values;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    const typed = t5;
    let t6;
    if ($[17] !== filter.values || $[18] !== locale || $[19] !== typed) {
      t6 = typed ? dayRangeLabel(filter.values, locale) : relativeDateLabel(filter.values, locale);
      $[17] = filter.values;
      $[18] = locale;
      $[19] = typed;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] !== t6) {
      t7 = <span>{t6}</span>;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  let t5;
  if ($[23] !== filter.values) {
    t5 = resolveDateFilterValues(filter.values);
    $[23] = filter.values;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const [from, to] = t5;
  let t6;
  if ($[25] !== from || $[26] !== to) {
    t6 = <DateFormatter from={from} kind="compact-range" to={to} data-sentry-element="DateFormatter" data-sentry-component="FilterValueDateDisplay" data-sentry-source-file="filter-value.tsx" />;
    $[25] = from;
    $[26] = to;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
}
export function FilterValueDisplay(t0) {
  const $ = _c(40);
  const {
    actions,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  if (isColumnOfType(column, "amount") && filter.type === "amount") {
    let t2;
    if ($[0] !== actions || $[1] !== column || $[2] !== filter || $[3] !== locale) {
      t2 = <FilterValueAmountDisplay actions={actions} column={column} filter={filter} locale={locale} />;
      $[0] = actions;
      $[1] = column;
      $[2] = filter;
      $[3] = locale;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (isColumnOfType(column, "company") && filter.type === "company") {
    let t2;
    if ($[5] !== actions || $[6] !== column || $[7] !== filter || $[8] !== locale) {
      t2 = <FilterValueCompanyDisplay actions={actions} column={column} filter={filter} locale={locale} />;
      $[5] = actions;
      $[6] = column;
      $[7] = filter;
      $[8] = locale;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  if (isColumnOfType(column, "date") && filter.type === "date") {
    let t2;
    if ($[10] !== actions || $[11] !== column || $[12] !== filter || $[13] !== locale) {
      t2 = <FilterValueDateDisplay actions={actions} column={column} filter={filter} locale={locale} />;
      $[10] = actions;
      $[11] = column;
      $[12] = filter;
      $[13] = locale;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    return t2;
  }
  if (isColumnOfType(column, "location") && filter.type === "location") {
    let t2;
    if ($[15] !== actions || $[16] !== column || $[17] !== filter || $[18] !== locale) {
      t2 = <FilterValueLocationDisplay actions={actions} column={column} filter={filter} locale={locale} />;
      $[15] = actions;
      $[16] = column;
      $[17] = filter;
      $[18] = locale;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    return t2;
  }
  if (isColumnOfType(column, "multiOption") && filter.type === "multiOption") {
    let t2;
    if ($[20] !== actions || $[21] !== column || $[22] !== filter || $[23] !== locale) {
      t2 = <FilterValueMultiOptionDisplay actions={actions} column={column} filter={filter} locale={locale} />;
      $[20] = actions;
      $[21] = column;
      $[22] = filter;
      $[23] = locale;
      $[24] = t2;
    } else {
      t2 = $[24];
    }
    return t2;
  }
  if (isColumnOfType(column, "number") && filter.type === "number") {
    let t2;
    if ($[25] !== actions || $[26] !== column || $[27] !== filter || $[28] !== locale) {
      t2 = <FilterValueNumberDisplay actions={actions} column={column} filter={filter} locale={locale} />;
      $[25] = actions;
      $[26] = column;
      $[27] = filter;
      $[28] = locale;
      $[29] = t2;
    } else {
      t2 = $[29];
    }
    return t2;
  }
  if (isColumnOfType(column, "option") && filter.type === "option") {
    let t2;
    if ($[30] !== actions || $[31] !== column || $[32] !== filter || $[33] !== locale) {
      t2 = <FilterValueOptionDisplay actions={actions} column={column} filter={filter} locale={locale} />;
      $[30] = actions;
      $[31] = column;
      $[32] = filter;
      $[33] = locale;
      $[34] = t2;
    } else {
      t2 = $[34];
    }
    return t2;
  }
  if (isColumnOfType(column, "text") && filter.type === "text") {
    let t2;
    if ($[35] !== actions || $[36] !== column || $[37] !== filter || $[38] !== locale) {
      t2 = <FilterValueTextDisplay actions={actions} column={column} filter={filter} locale={locale} />;
      $[35] = actions;
      $[36] = column;
      $[37] = filter;
      $[38] = locale;
      $[39] = t2;
    } else {
      t2 = $[39];
    }
    return t2;
  }
  return null;
}
export function FilterValueLocationDisplay(t0) {
  const $ = _c(3);
  const {
    filter,
    locale: t1
  } = t0;
  t1 === undefined ? "en" : t1;
  if (!filter || !filter.values || filter.values.length === 0) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Ellipsis className="size-4" />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  const value = filter.values[0];
  let t2;
  if ($[1] !== value.label) {
    t2 = <span data-sentry-component="FilterValueLocationDisplay" data-sentry-source-file="filter-value.tsx">{value.label}</span>;
    $[1] = value.label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function FilterValueMultiOptionDisplay(t0) {
  const $ = _c(28);
  const {
    column,
    filter,
    locale: t1
  } = t0;
  t1 === undefined ? "en" : t1;
  let t2;
  if ($[0] !== column) {
    t2 = column.getOptions();
    $[0] = column;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const options = t2;
  let name;
  let selected;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] !== column.displayName || $[3] !== filter || $[4] !== options) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t8;
      if ($[12] !== filter) {
        t8 = o => filter.values.includes(o.value);
        $[12] = filter;
        $[13] = t8;
      } else {
        t8 = $[13];
      }
      selected = options.filter(t8);
      if (selected.length === 0) {
        let t9;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <Ellipsis className="size-4" />;
          $[14] = t9;
        } else {
          t9 = $[14];
        }
        t7 = t9;
        break bb0;
      }
      if (selected.length === 1) {
        const {
          icon: Icon,
          label
        } = selected[0];
        const hasIcon = !!Icon;
        t7 = <span className="inline-flex items-center gap-1.5">{hasIcon && (isValidElement(Icon) ? Icon : <Icon className="size-4 text-primary" />)}<span>{label}</span></span>;
        break bb0;
      }
      let t9;
      if ($[15] !== column.displayName) {
        t9 = column.displayName.toLowerCase();
        $[15] = column.displayName;
        $[16] = t9;
      } else {
        t9 = $[16];
      }
      name = t9;
      let t10;
      if ($[17] !== options) {
        t10 = options?.some(_temp4);
        $[17] = options;
        $[18] = t10;
      } else {
        t10 = $[18];
      }
      const hasOptionIcons = !t10;
      t3 = "inline-flex items-center gap-1.5";
      t4 = "FilterValueMultiOptionDisplay";
      t5 = "filter-value.tsx";
      t6 = hasOptionIcons && <div className="inline-flex items-center gap-0.5" key="icons">{take(selected, 3).map(_temp5)}</div>;
    }
    $[2] = column.displayName;
    $[3] = filter;
    $[4] = options;
    $[5] = name;
    $[6] = selected;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    name = $[5];
    selected = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[19] !== name || $[20] !== selected.length) {
    t8 = <span>{selected.length} {name}</span>;
    $[19] = name;
    $[20] = selected.length;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t8) {
    t9 = <div className={t3} data-sentry-component={t4} data-sentry-source-file={t5}>{t6}{t8}</div>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
}
function _temp5(t0) {
  const {
    icon,
    value
  } = t0;
  const Icon_0 = icon;
  return isValidElement(Icon_0) ? cloneElement(Icon_0, {
    key: value
  }) : <Icon_0 className="size-4" key={value} />;
}
function _temp4(o_0) {
  return !o_0.icon;
}
export function FilterValueNumberDisplay(t0) {
  const $ = _c(10);
  const {
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  if (!filter || !filter.values || filter.values.length === 0) {
    return null;
  }
  const unit = column.unit;
  if (filter.operator === "is between" || filter.operator === "is not between") {
    const minValue = filter.values[0];
    const maxValue = filter.values[1];
    let t2;
    if ($[0] !== locale) {
      t2 = t("and", locale);
      $[0] = locale;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    const t3 = unit && ` ${unit}`;
    let t4;
    if ($[2] !== maxValue || $[3] !== minValue || $[4] !== t2 || $[5] !== t3) {
      t4 = <span className="tracking-tight tabular-nums">{minValue} {t2} {maxValue}{t3}</span>;
      $[2] = maxValue;
      $[3] = minValue;
      $[4] = t2;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  const value = filter.values[0];
  const t2 = unit && ` ${unit}`;
  let t3;
  if ($[7] !== t2 || $[8] !== value) {
    t3 = <span className="tracking-tight tabular-nums" data-sentry-component="FilterValueNumberDisplay" data-sentry-source-file="filter-value.tsx">{value}{t2}</span>;
    $[7] = t2;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
export function FilterValueOptionDisplay(t0) {
  const $ = _c(24);
  const {
    column,
    filter,
    locale: t1
  } = t0;
  t1 === undefined ? "en" : t1;
  let hasOptionIcons;
  let pluralName;
  let selected;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== column || $[1] !== filter?.values) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const options = column.getOptions();
      const custom = customNumberOption(column, filter?.values);
      selected = custom ? [custom] : options.filter(o => filter?.values.includes(o.value));
      if (selected.length === 1) {
        const {
          icon: Icon,
          label
        } = selected[0];
        const hasIcon = !!Icon;
        t6 = <span className="inline-flex items-center gap-1">{hasIcon && (isValidElement(Icon) ? Icon : <Icon className="size-4 text-primary" />)}<span>{label}</span></span>;
        break bb0;
      }
      let t7;
      if ($[10] !== column.displayName) {
        const name = column.displayName.toLowerCase();
        t7 = name.endsWith("s") ? `${name}es` : `${name}s`;
        $[10] = column.displayName;
        $[11] = t7;
      } else {
        t7 = $[11];
      }
      pluralName = t7;
      hasOptionIcons = !options?.some(_temp6);
      t2 = "inline-flex items-center gap-0.5";
      t3 = "FilterValueOptionDisplay";
      t4 = "filter-value.tsx";
      t5 = hasOptionIcons && take(selected, 3).map(_temp7);
    }
    $[0] = column;
    $[1] = filter?.values;
    $[2] = hasOptionIcons;
    $[3] = pluralName;
    $[4] = selected;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    hasOptionIcons = $[2];
    pluralName = $[3];
    selected = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  const t7 = hasOptionIcons && "ml-1.5";
  let t8;
  if ($[12] !== t7) {
    t8 = cn(t7);
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== pluralName || $[15] !== selected.length || $[16] !== t8) {
    t9 = <span className={t8}>{selected.length} {pluralName}</span>;
    $[14] = pluralName;
    $[15] = selected.length;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t9) {
    t10 = <div className={t2} data-sentry-component={t3} data-sentry-source-file={t4}>{t5}{t9}</div>;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function _temp7(t0) {
  const {
    icon,
    value
  } = t0;
  const Icon_0 = icon;
  return isValidElement(Icon_0) ? Icon_0 : <Icon_0 className="size-4" key={value} />;
}
function _temp6(o_0) {
  return !o_0.icon;
}
export function FilterValueTextDisplay(t0) {
  const $ = _c(3);
  const {
    filter,
    locale: t1
  } = t0;
  t1 === undefined ? "en" : t1;
  if (!filter) {
    return null;
  }
  if (filter.values.length === 0 || filter.values[0].trim() === "") {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Ellipsis className="size-4" />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  const value = filter.values[0];
  let t2;
  if ($[1] !== value) {
    t2 = <span data-sentry-component="FilterValueTextDisplay" data-sentry-source-file="filter-value.tsx">{value}</span>;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function __FilterValue<TData, TType extends ColumnDataType>({
  actions,
  column,
  filter,
  locale,
  strategy
}: FilterValueProps<TData, TType>) {
  return <Popover data-sentry-element="Popover" data-sentry-component="__FilterValue" data-sentry-source-file="filter-value.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="filter-value.tsx">
        <Button className="m-0 h-full w-fit rounded-none p-0 px-2 text-xs whitespace-nowrap" variant="ghost" data-sentry-element="Button" data-sentry-source-file="filter-value.tsx">
          <PopoverAnchor className="h-full" data-sentry-element="PopoverAnchor" data-sentry-source-file="filter-value.tsx" />
          <FilterValueDisplay actions={actions} column={column} filter={filter} locale={locale} data-sentry-element="FilterValueDisplay" data-sentry-source-file="filter-value.tsx" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-fit origin-(--radix-popover-content-transform-origin) p-0" side="bottom" data-sentry-element="PopoverContent" data-sentry-source-file="filter-value.tsx">
        <FilterValueController actions={actions} column={column} filter={filter} locale={locale} strategy={strategy} data-sentry-element="FilterValueController" data-sentry-source-file="filter-value.tsx" />
      </PopoverContent>
    </Popover>;
}

/****** Property Filter Value Controller ******/

export const FilterValueController =
// SAFETY: the memo wrapper renders the component with the same props and is only used in JSX;
// memo's typings would drop the TData generic.
memo(__FilterValueController) as typeof __FilterValueController;
interface OptionItemProps {
  onToggle: (value: string, checked: boolean) => void;
  option: ColumnOptionExtended;
}
function __FilterValueController<TData>({
  actions,
  column,
  filter,
  locale = 'en',
  strategy
}: FilterValueControllerProps<TData>) {
  if (isColumnOfType(column, 'amount')) {
    return <FilterValueAmountController actions={actions} column={column} filter={filter?.type === 'amount' ? filter : undefined} locale={locale} strategy={strategy} />;
  }
  if (isColumnOfType(column, 'company')) {
    return <FilterValueCompanyController actions={actions} column={column} filter={filter?.type === 'company' ? filter : undefined} locale={locale} strategy={strategy} />;
  }
  if (isColumnOfType(column, 'date')) {
    return <FilterValueDateController actions={actions} column={column} filter={filter?.type === 'date' ? filter : undefined} locale={locale} strategy={strategy} />;
  }
  if (isColumnOfType(column, 'location')) {
    return <FilterValueLocationController actions={actions} column={column} filter={filter?.type === 'location' ? filter : undefined} locale={locale} strategy={strategy} />;
  }
  if (isColumnOfType(column, 'multiOption')) {
    return <FilterValueMultiOptionController actions={actions} column={column} filter={filter?.type === 'multiOption' ? filter : undefined} locale={locale} strategy={strategy} />;
  }
  if (isColumnOfType(column, 'number')) {
    return <FilterValueNumberController actions={actions} column={column} filter={filter?.type === 'number' ? filter : undefined} locale={locale} strategy={strategy} />;
  }
  if (isColumnOfType(column, 'option')) {
    return <FilterValueOptionController actions={actions} column={column} filter={filter?.type === 'option' ? filter : undefined} locale={locale} strategy={strategy} />;
  }
  if (isColumnOfType(column, 'text')) {
    return <FilterValueTextController actions={actions} column={column} filter={filter?.type === 'text' ? filter : undefined} locale={locale} strategy={strategy} />;
  }
  return null;
}

// Memoized option item to prevent re-renders unless its own props change
const OptionItem = memo(function OptionItem(t0) {
  const $ = _c(21);
  const {
    onToggle,
    option
  } = t0;
  const {
    icon: Icon,
    label,
    selected,
    value
  } = option;
  let t1;
  if ($[0] !== onToggle || $[1] !== selected || $[2] !== value) {
    t1 = () => {
      onToggle(value, !selected);
    };
    $[0] = onToggle;
    $[1] = selected;
    $[2] = value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleSelect = t1;
  let t2;
  if ($[4] !== label) {
    t2 = [label];
    $[4] = label;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== selected) {
    t3 = <Checkbox checked={selected} />;
    $[6] = selected;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== Icon) {
    t4 = Icon && (isValidElement(Icon) ? Icon : <Icon className="size-4 text-primary" />);
    $[8] = Icon;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== label) {
    t5 = <span>{label}</span>;
    $[10] = label;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <div className="flex items-center gap-1.5">{t3}{t4}{t5}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== handleSelect || $[17] !== t2 || $[18] !== t6 || $[19] !== value) {
    t7 = <CommandItem className="group flex items-center justify-between gap-1.5" key={value} keywords={t2} onSelect={handleSelect} value={value}>{t6}</CommandItem>;
    $[16] = handleSelect;
    $[17] = t2;
    $[18] = t6;
    $[19] = value;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
});
export function FilterValueCompanyController(t0) {
  const $ = _c(9);
  const {
    actions,
    column,
    filter,
    locale: t1
  } = t0;
  t1 === undefined ? "en" : t1;
  const autoFocus = use(FilterEditorAutoFocus);
  const CompanyFilterComponent = column.filterComponent;
  const selectedCompany = filter?.values?.[0] ?? null;
  if (!CompanyFilterComponent) {
    throw new Error("Company filter requires a filterComponent");
  }
  let t2;
  if ($[0] !== actions || $[1] !== column) {
    t2 = newValue => actions.setFilterValue(column, newValue ? [newValue] : []);
    $[0] = actions;
    $[1] = column;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== CompanyFilterComponent || $[4] !== autoFocus || $[5] !== column.id || $[6] !== selectedCompany || $[7] !== t2) {
    t3 = <Command data-sentry-element="Command" data-sentry-component="FilterValueCompanyController" data-sentry-source-file="filter-value.tsx"><CommandList className="max-h-fit" data-sentry-element="CommandList" data-sentry-source-file="filter-value.tsx"><CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx"><CompanyFilterComponent autoFocus={autoFocus} id={column.id} multiple={false} onChange={t2} value={selectedCompany} data-sentry-element="CompanyFilterComponent" data-sentry-source-file="filter-value.tsx" /></CommandGroup></CommandList></Command>;
    $[3] = CompanyFilterComponent;
    $[4] = autoFocus;
    $[5] = column.id;
    $[6] = selectedCompany;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/*
 * The Relative sub-tab a filter opens on: the one whose choice set it. A typed range is a window,
 * except on columns with day counts, whose Range tab holds it.
 */
const relativeSubTab = (values: RelativeDate[], dayCounts?: boolean) => {
  if (findMonthChoice(values)) return 'months';
  const typedWindow = !dayCounts && values.length > 0 && !isRelativeDateChoice(values);
  return typedWindow ? 'window' : 'days';
};
export function FilterValueDateController(t0) {
  const $ = _c(94);
  const {
    actions,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const autoFocus = use(FilterEditorAutoFocus);
  let t2;
  if ($[0] !== filter) {
    t2 = filter?.values.every(isAbsoluteDate) ? filter.values : [];
    $[0] = filter;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const absolute = t2;
  let t3;
  if ($[2] !== absolute || $[3] !== filter?.values.length) {
    t3 = filter?.values.length && absolute.length === 0 ? undefined : {
      from: absolute[0] ?? new Date(),
      to: absolute[1] ?? undefined
    };
    $[2] = absolute;
    $[3] = filter?.values.length;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [date, setDate] = useState(t3);
  let t4;
  if ($[5] !== column || $[6] !== filter) {
    t4 = overdueDays(column, filter);
    $[5] = column;
    $[6] = filter;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const pastDue = t4;
  let t5;
  if ($[8] !== actions || $[9] !== column || $[10] !== pastDue) {
    t5 = function setValues(values) {
      if (pastDue === undefined) {
        actions.setFilterValue(column, values);
      } else {
        actions.setFilter(column, values.length > 1 ? "is between" : "is", values);
      }
    };
    $[8] = actions;
    $[9] = column;
    $[10] = pastDue;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const setValues = t5;
  let t6;
  if ($[12] !== setValues) {
    t6 = function changeDateRange(value) {
      const {
        from: start,
        to: end
      } = collapseSameDayRange(value);
      setDate({
        from: start,
        to: end
      });
      const isRange = start && end;
      const newValues = isRange ? [start, end] : start ? [start] : [];
      setValues(newValues);
    };
    $[12] = setValues;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const changeDateRange = t6;
  let t7;
  if ($[14] !== setValues) {
    t7 = function chooseRelative(values_0) {
      setDate(undefined);
      setValues(values_0);
    };
    $[14] = setValues;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const chooseRelative = t7;
  let t8;
  if ($[16] !== date?.from) {
    t8 = () => date?.from ?? new Date();
    $[16] = date?.from;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const [month, setMonth] = useState(t8);
  let t9;
  if ($[18] !== actions || $[19] !== column) {
    t9 = function chooseOverdue(days) {
      setDate(undefined);
      const {
        operator,
        values: values_1
      } = overdueChoice(days);
      actions.setFilter(column, operator, values_1);
    };
    $[18] = actions;
    $[19] = column;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const chooseOverdue = t9;
  const exactTab = column.dayCounts ? "range" : "calendar";
  let t10;
  if ($[21] !== filter) {
    t10 = filter?.values.every(isRelativeDate) ? filter.values : [];
    $[21] = filter;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const relative = t10;
  const opensExact = column.dayCounts ? relative.length > 0 && !isRelativeDateChoice(relative) : absolute.length > 0;
  let t11;
  if ($[23] !== column.dayCounts || $[24] !== relative) {
    t11 = relativeSubTab(relative, column.dayCounts);
    $[23] = column.dayCounts;
    $[24] = relative;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const relativeTab = t11;
  const t12 = opensExact ? exactTab : "relative";
  let t13;
  if ($[26] !== locale) {
    t13 = t("relative", locale);
    $[26] = locale;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13) {
    t14 = <TabsTrigger value="relative" data-sentry-element="TabsTrigger" data-sentry-source-file="filter-value.tsx">{t13}</TabsTrigger>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== exactTab || $[31] !== locale) {
    t15 = t(exactTab, locale);
    $[30] = exactTab;
    $[31] = locale;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== exactTab || $[34] !== t15) {
    t16 = <TabsTrigger value={exactTab} data-sentry-element="TabsTrigger" data-sentry-source-file="filter-value.tsx">{t15}</TabsTrigger>;
    $[33] = exactTab;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t16) {
    t17 = <TabsList className="mx-2 *:text-xs" data-sentry-element="TabsList" data-sentry-source-file="filter-value.tsx">{t14}{t16}</TabsList>;
    $[36] = t14;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== locale) {
    t18 = t("days", locale);
    $[39] = locale;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18) {
    t19 = <TabsTrigger value="days" data-sentry-element="TabsTrigger" data-sentry-source-file="filter-value.tsx">{t18}</TabsTrigger>;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== locale) {
    t20 = t("months", locale);
    $[43] = locale;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t20) {
    t21 = <TabsTrigger value="months" data-sentry-element="TabsTrigger" data-sentry-source-file="filter-value.tsx">{t20}</TabsTrigger>;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== column.dayCounts || $[48] !== locale) {
    t22 = column.dayCounts ? null : <TabsTrigger value="window">{t("window", locale)}</TabsTrigger>;
    $[47] = column.dayCounts;
    $[48] = locale;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t19 || $[51] !== t21 || $[52] !== t22) {
    t23 = <TabsList className="mx-2 *:text-xs" data-sentry-element="TabsList" data-sentry-source-file="filter-value.tsx">{t19}{t21}{t22}</TabsList>;
    $[50] = t19;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  const t24 = column.overdueChoices ? chooseOverdue : undefined;
  let t25;
  if ($[54] !== chooseRelative || $[55] !== filter || $[56] !== locale || $[57] !== pastDue || $[58] !== t24) {
    t25 = <TabsContent value="days" data-sentry-element="TabsContent" data-sentry-source-file="filter-value.tsx"><RelativeDateChoices filter={filter} locale={locale} onChoose={chooseRelative} onChooseOverdue={t24} pastDue={pastDue} data-sentry-element="RelativeDateChoices" data-sentry-source-file="filter-value.tsx" /></TabsContent>;
    $[54] = chooseRelative;
    $[55] = filter;
    $[56] = locale;
    $[57] = pastDue;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== chooseRelative || $[61] !== locale || $[62] !== relative) {
    t26 = <TabsContent value="months" data-sentry-element="TabsContent" data-sentry-source-file="filter-value.tsx"><MonthChoices current={relative} locale={locale} onChoose={chooseRelative} data-sentry-element="MonthChoices" data-sentry-source-file="filter-value.tsx" /></TabsContent>;
    $[60] = chooseRelative;
    $[61] = locale;
    $[62] = relative;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== chooseRelative || $[65] !== column.dayCounts || $[66] !== filter || $[67] !== locale || $[68] !== pastDue) {
    t27 = column.dayCounts ? null : <TabsContent value="window"><WindowInputs apply={chooseRelative} filter={pastDue === undefined ? filter : undefined} locale={locale} /></TabsContent>;
    $[64] = chooseRelative;
    $[65] = column.dayCounts;
    $[66] = filter;
    $[67] = locale;
    $[68] = pastDue;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== relativeTab || $[71] !== t23 || $[72] !== t25 || $[73] !== t26 || $[74] !== t27) {
    t28 = <TabsContent value="relative" data-sentry-element="TabsContent" data-sentry-source-file="filter-value.tsx"><Tabs className="flex flex-col" defaultValue={relativeTab} data-sentry-element="Tabs" data-sentry-source-file="filter-value.tsx">{t23}{t25}{t26}{t27}</Tabs></TabsContent>;
    $[70] = relativeTab;
    $[71] = t23;
    $[72] = t25;
    $[73] = t26;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== autoFocus || $[77] !== changeDateRange || $[78] !== column.dayCounts || $[79] !== date || $[80] !== filter || $[81] !== locale || $[82] !== month || $[83] !== pastDue || $[84] !== setValues) {
    t29 = column.dayCounts ? <DayRangeInputs apply={setValues} filter={pastDue === undefined ? filter : undefined} locale={locale} /> : <CommandGroup><div><Calendar autoFocus={autoFocus} mode="range" month={month} numberOfMonths={1} onMonthChange={setMonth} onSelect={changeDateRange} selected={date} /></div><CommandItem className="justify-center" onSelect={() => changeDateRange({
        from: startOfMonth(month),
        to: lastDayOfMonth(month)
      })}>{t("wholeMonth", locale)}</CommandItem></CommandGroup>;
    $[76] = autoFocus;
    $[77] = changeDateRange;
    $[78] = column.dayCounts;
    $[79] = date;
    $[80] = filter;
    $[81] = locale;
    $[82] = month;
    $[83] = pastDue;
    $[84] = setValues;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== exactTab || $[87] !== t29) {
    t30 = <TabsContent value={exactTab} data-sentry-element="TabsContent" data-sentry-source-file="filter-value.tsx">{t29}</TabsContent>;
    $[86] = exactTab;
    $[87] = t29;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  let t31;
  if ($[89] !== t12 || $[90] !== t17 || $[91] !== t28 || $[92] !== t30) {
    t31 = <Command data-sentry-element="Command" data-sentry-component="FilterValueDateController" data-sentry-source-file="filter-value.tsx"><CommandList className="max-h-fit" data-sentry-element="CommandList" data-sentry-source-file="filter-value.tsx"><Tabs className="flex flex-col pt-2" defaultValue={t12} data-sentry-element="Tabs" data-sentry-source-file="filter-value.tsx">{t17}{t28}{t30}</Tabs></CommandList></Command>;
    $[89] = t12;
    $[90] = t17;
    $[91] = t28;
    $[92] = t30;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  return t31;
}
export function FilterValueLocationController(t0) {
  const $ = _c(9);
  const {
    actions,
    column,
    filter,
    locale: t1
  } = t0;
  t1 === undefined ? "en" : t1;
  const autoFocus = use(FilterEditorAutoFocus);
  const LocationFilterComponent = column.filterComponent;
  const selectedLocation = filter?.values?.[0] ?? null;
  if (!LocationFilterComponent) {
    throw new Error("Location filter requires a filterComponent");
  }
  let t2;
  if ($[0] !== actions || $[1] !== column) {
    t2 = newValue => actions.setFilterValue(column, newValue ? [newValue] : []);
    $[0] = actions;
    $[1] = column;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== LocationFilterComponent || $[4] !== autoFocus || $[5] !== column.id || $[6] !== selectedLocation || $[7] !== t2) {
    t3 = <Command data-sentry-element="Command" data-sentry-component="FilterValueLocationController" data-sentry-source-file="filter-value.tsx"><CommandList className="max-h-fit" data-sentry-element="CommandList" data-sentry-source-file="filter-value.tsx"><CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx"><LocationFilterComponent autoFocus={autoFocus} id={column.id} multiple={false} onChange={t2} value={selectedLocation} data-sentry-element="LocationFilterComponent" data-sentry-source-file="filter-value.tsx" /></CommandGroup></CommandList></Command>;
    $[3] = LocationFilterComponent;
    $[4] = autoFocus;
    $[5] = column.id;
    $[6] = selectedLocation;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
export function FilterValueMultiOptionController(t0) {
  const $ = _c(63);
  const {
    actions,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const autoFocus = use(FilterEditorAutoFocus);
  let t2;
  if ($[0] !== filter?.values) {
    t2 = () => filter?.values ?? [];
    $[0] = filter?.values;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [initialValues] = useState(t2);
  let t3;
  if ($[2] !== column || $[3] !== filter?.values || $[4] !== initialValues) {
    const counts = column.getFacetedUniqueValues();
    t3 = column.getOptions().map(o => ({
      ...o,
      count: counts?.get(o.value) ?? 0,
      initialSelected: initialValues.includes(o.value),
      selected: filter?.values.includes(o.value)
    }));
    $[2] = column;
    $[3] = filter?.values;
    $[4] = initialValues;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const options = t3;
  let t4;
  if ($[6] !== actions || $[7] !== column) {
    t4 = (value, checked) => {
      if (checked) {
        actions.addFilterValue(column, [value]);
      } else {
        actions.removeFilterValue(column, [value]);
      }
    };
    $[6] = actions;
    $[7] = column;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleToggle = t4;
  let t5;
  if ($[9] !== options) {
    const sel = [];
    const unsel = [];
    for (const o_0 of options) {
      if (o_0.initialSelected) {
        sel.push(o_0);
      } else {
        unsel.push(o_0);
      }
    }
    t5 = {
      selectedOptions: sel,
      unselectedOptions: unsel
    };
    $[9] = options;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    selectedOptions,
    unselectedOptions
  } = t5;
  const selectAllNone = column.selectAllNone;
  let allValues;
  let t6;
  if ($[11] !== filter?.values || $[12] !== options) {
    allValues = options.map(_temp8);
    let t7;
    if ($[15] !== filter?.values) {
      t7 = value_0 => filter?.values.includes(value_0);
      $[15] = filter?.values;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t6 = allValues.every(t7);
    $[11] = filter?.values;
    $[12] = options;
    $[13] = allValues;
    $[14] = t6;
  } else {
    allValues = $[13];
    t6 = $[14];
  }
  const allSelected = t6;
  const onlyNoneSelected = filter?.values.length === 1 && filter.values[0] === selectAllNone?.noneValue;
  let t7;
  if ($[17] !== locale) {
    t7 = t("search", locale);
    $[17] = locale;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== autoFocus || $[20] !== t7) {
    t8 = <CommandInput autoFocus={autoFocus} placeholder={t7} data-sentry-element="CommandInput" data-sentry-source-file="filter-value.tsx" />;
    $[19] = autoFocus;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== locale) {
    t9 = t("noresults", locale);
    $[22] = locale;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="filter-value.tsx">{t9}</CommandEmpty>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== actions || $[27] !== allSelected || $[28] !== allValues || $[29] !== column || $[30] !== locale || $[31] !== onlyNoneSelected || $[32] !== selectAllNone) {
    t11 = selectAllNone && <><CommandGroup><CommandItem disabled={allSelected} onSelect={() => actions.setFilterValue(column, allValues)}>{t("selectAll", locale)}</CommandItem><CommandItem disabled={onlyNoneSelected} onSelect={() => actions.setFilterValue(column, [selectAllNone.noneValue])}>{t("selectNone", locale)}</CommandItem></CommandGroup><CommandSeparator /></>;
    $[26] = actions;
    $[27] = allSelected;
    $[28] = allValues;
    $[29] = column;
    $[30] = locale;
    $[31] = onlyNoneSelected;
    $[32] = selectAllNone;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const t12 = selectedOptions.length === 0 && "hidden";
  let t13;
  if ($[34] !== t12) {
    t13 = cn(t12);
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== handleToggle || $[37] !== selectedOptions) {
    let t15;
    if ($[39] !== handleToggle) {
      t15 = option => <OptionItem key={option.value} onToggle={handleToggle} option={option} />;
      $[39] = handleToggle;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    t14 = selectedOptions.map(t15);
    $[36] = handleToggle;
    $[37] = selectedOptions;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[41] !== t13 || $[42] !== t14) {
    t15 = <CommandGroup className={t13} data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx">{t14}</CommandGroup>;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <CommandSeparator data-sentry-element="CommandSeparator" data-sentry-source-file="filter-value.tsx" />;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const t17 = unselectedOptions.length === 0 && "hidden";
  let t18;
  if ($[45] !== t17) {
    t18 = cn(t17);
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== handleToggle || $[48] !== unselectedOptions) {
    let t20;
    if ($[50] !== handleToggle) {
      t20 = option_0 => <OptionItem key={option_0.value} onToggle={handleToggle} option={option_0} />;
      $[50] = handleToggle;
      $[51] = t20;
    } else {
      t20 = $[51];
    }
    t19 = unselectedOptions.map(t20);
    $[47] = handleToggle;
    $[48] = unselectedOptions;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[52] !== t18 || $[53] !== t19) {
    t20 = <CommandGroup className={t18} data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx">{t19}</CommandGroup>;
    $[52] = t18;
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== t11 || $[56] !== t15 || $[57] !== t20) {
    t21 = <CommandList data-sentry-element="CommandList" data-sentry-source-file="filter-value.tsx">{t11}{t15}{t16}{t20}</CommandList>;
    $[55] = t11;
    $[56] = t15;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== t10 || $[60] !== t21 || $[61] !== t8) {
    t22 = <Command loop={true} data-sentry-element="Command" data-sentry-component="FilterValueMultiOptionController" data-sentry-source-file="filter-value.tsx">{t8}{t10}{t21}</Command>;
    $[59] = t10;
    $[60] = t21;
    $[61] = t8;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  return t22;
}

/* The operator for the bounds set on the Range tab: Min alone is "at least", Max alone "at most". */
function _temp8(o_1) {
  return o_1.value;
}
function rangeOperator(min: null | number, max: null | number): FilterOperators['amount'] {
  if (min !== null && max !== null) return 'is between';
  if (min !== null) return 'is greater than or equal to';
  if (max !== null) return 'is less than or equal to';
  return 'is';
}

/* The operators the Range tab sets, so a filter with one of them reopens on the Range tab. */
const RANGE_TAB_OPERATORS: ReadonlySet<FilterOperators['amount']> = new Set(['is between', 'is greater than or equal to', 'is less than or equal to']);
interface CurrencySelectProps {
  className?: string;
  currencies: ColumnOption[];
  locale: Locale;
  /* Empty for any currency. */
  onChange: (currencies: string[]) => void;
  /* Runs once the menu has closed. */
  onClose?: () => void;
  value: string[];
  variant?: 'ghost' | 'outline';
}

/*
 * An amount with optional currencies in one filter. The amount is optional too, so currencies on
 * their own filter by currency; clearing both removes the filter. Inputs are debounced by
 * DebouncedInput, the currencies apply at once.
 */
export function FilterValueAmountController(t0) {
  const $ = _c(29);
  const {
    actions,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  let t2;
  if ($[0] !== filter) {
    t2 = filter ? RANGE_TAB_OPERATORS.has(filter.operator) : false;
    $[0] = filter;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [isRange, setIsRange] = useState(t2);
  let t3;
  if ($[2] !== filter) {
    t3 = () => initialAmounts(filter);
    $[2] = filter;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [amounts, setAmounts] = useState(t3);
  let t4;
  if ($[4] !== filter?.values[0]?.currencies) {
    t4 = filter?.values[0]?.currencies ?? [];
    $[4] = filter?.values[0]?.currencies;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [currencies, setCurrencies] = useState(t4);
  const pendingInput = useRef({
    amounts,
    currencies
  });
  let t5;
  if ($[6] !== actions || $[7] !== column || $[8] !== filter) {
    t5 = (nextAmounts, nextCurrencies) => {
      const [first, t6] = nextAmounts;
      const second = t6 === undefined ? null : t6;
      const isRangeInput = nextAmounts.length === 2;
      if (first === null && second === null && nextCurrencies.length === 0) {
        actions.removeFilter(column.id);
        return;
      }
      const bounds = [first, second].filter(_temp9);
      const currentOperator = filter && numberFilterOperators[filter.operator].target === "single" ? filter.operator : "is";
      const operator = isRangeInput ? rangeOperator(first, second) : currentOperator;
      actions.setFilterValue(column, bounds.length === 0 ? [{
        amount: null,
        currencies: nextCurrencies
      }] : bounds.map(amount_0 => ({
        amount: amount_0,
        currencies: nextCurrencies
      })));
      actions.setFilterOperator(column, operator);
    };
    $[6] = actions;
    $[7] = column;
    $[8] = filter;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const commitFilter = t5;
  const changeAmount = (index, value) => {
    const parsed = value === "" ? null : Number(value);
    const nextAmounts_0 = [...pendingInput.current.amounts];
    nextAmounts_0[index] = parsed === null || Number.isNaN(parsed) ? null : parsed;
    pendingInput.current.amounts = nextAmounts_0;
    setAmounts(nextAmounts_0);
    commitFilter(nextAmounts_0, pendingInput.current.currencies);
  };
  const changeCurrencies = nextCurrencies_0 => {
    pendingInput.current.currencies = nextCurrencies_0;
    setCurrencies(nextCurrencies_0);
    commitFilter(pendingInput.current.amounts, nextCurrencies_0);
  };
  const changeType = type => {
    const [t6, t7] = pendingInput.current.amounts;
    const min = t6 === undefined ? null : t6;
    const max = t7 === undefined ? null : t7;
    const nextAmounts_1 = type === "single" ? [min ?? max] : rangeAmounts(filter?.operator, min);
    pendingInput.current.amounts = nextAmounts_1;
    setIsRange(type === "range");
    setAmounts(nextAmounts_1);
    commitFilter(nextAmounts_1, pendingInput.current.currencies);
  };
  const amountInput = (index_0, placeholder) => <DebouncedInput aria-label={placeholder} className="w-full" onChange={value_0 => changeAmount(index_0, value_0)} placeholder={placeholder} type="number" value={amounts[index_0] ?? ""} data-sentry-element="DebouncedInput" data-sentry-component="amountInput" data-sentry-source-file="filter-value.tsx" />;
  let t8;
  if ($[10] !== column) {
    t8 = column.getOptions();
    $[10] = column;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const currencySelect = <CurrencySelect className="shrink-0 font-normal" currencies={t8} locale={locale} onChange={changeCurrencies} value={currencies} />;
  let t9;
  if ($[12] !== locale) {
    t9 = t("single", locale);
    $[12] = locale;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <TabsTrigger value="single" data-sentry-element="TabsTrigger" data-sentry-source-file="filter-value.tsx">{t9}</TabsTrigger>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== locale) {
    t11 = t("range", locale);
    $[16] = locale;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = <TabsTrigger value="range" data-sentry-element="TabsTrigger" data-sentry-source-file="filter-value.tsx">{t11}</TabsTrigger>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12) {
    t13 = <TabsList className="w-full *:text-xs" data-sentry-element="TabsList" data-sentry-source-file="filter-value.tsx">{t10}{t12}</TabsList>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== locale) {
    t14 = t("amount", locale);
    $[23] = locale;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== locale) {
    t15 = t("min", locale);
    $[25] = locale;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = amountInput(0, t15);
  let t17;
  if ($[27] !== locale) {
    t17 = t("max", locale);
    $[27] = locale;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  return <Command data-sentry-element="Command" data-sentry-component="FilterValueAmountController" data-sentry-source-file="filter-value.tsx"><CommandList className="w-[300px] px-2 py-2" data-sentry-element="CommandList" data-sentry-source-file="filter-value.tsx"><CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx"><Tabs className="flex flex-col" onValueChange={value_1 => changeType(value_1 === "range" ? "range" : "single")} value={isRange ? "range" : "single"} data-sentry-element="Tabs" data-sentry-source-file="filter-value.tsx">{t13}<TabsContent className="mt-4" value="single" data-sentry-element="TabsContent" data-sentry-source-file="filter-value.tsx"><ButtonGroup className="w-full" data-sentry-element="ButtonGroup" data-sentry-source-file="filter-value.tsx">{amountInput(0, t14)}{currencySelect}</ButtonGroup></TabsContent><TabsContent className="mt-4" value="range" data-sentry-element="TabsContent" data-sentry-source-file="filter-value.tsx"><ButtonGroup className="w-full" data-sentry-element="ButtonGroup" data-sentry-source-file="filter-value.tsx">{t16}{amountInput(1, t17)}{currencySelect}</ButtonGroup></TabsContent></Tabs></CommandGroup></CommandList></Command>;
}
function _temp9(amount) {
  return amount !== null;
}
export function FilterValueNumberController<TData>({
  actions,
  column,
  filter,
  locale = 'en'
}: FilterValueControllerProps<TData, 'number'>) {
  const minMax = useMemo(() => column.getFacetedMinMaxValues(), [column]);
  const [sliderMin, sliderMax] = [minMax ? minMax[0] : 0, minMax ? minMax[1] : 0];

  // Local state for values
  const [values, setValues] = useState(filter?.values ?? [0, 0]);
  const isNumberRange =
  // filter && values.length === 2
  filter && numberFilterOperators[filter.operator].target === 'multiple';
  const setFilterOperatorDebounced = useDebounceCallback(actions.setFilterOperator, 500);
  // Explicit arguments keep the generic action typed for this number column.
  const setFilterValueDebounced = useDebounceCallback<[Column<TData, 'number'>, number[]], void>(actions.setFilterValue, 500);
  const changeNumber = (value: number[]) => {
    setValues(value);
    setFilterValueDebounced(column, value);
  };
  const changeMinNumber = (value_0: number) => {
    const newValues = createNumberRange([value_0, values[1]]);
    setValues(newValues);
    setFilterValueDebounced(column, newValues);
  };
  const changeMaxNumber = (value_1: number) => {
    const newValues_0 = createNumberRange([values[0], value_1]);
    setValues(newValues_0);
    setFilterValueDebounced(column, newValues_0);
  };
  const changeType = useCallback((type: 'range' | 'single') => {
    // eslint-disable-next-line no-useless-assignment
    let newValues_1: number[] = [];
    if (type === 'single') newValues_1 = [values[0]]; // Keep the first value for single mode
    else if (!minMax) newValues_1 = createNumberRange([values[0], values[1] ?? 0]);else {
      const value_2 = values[0];
      newValues_1 = value_2 - minMax[0] < minMax[1] - value_2 ? createNumberRange([value_2, minMax[1]]) : createNumberRange([minMax[0], value_2]);
    }
    const newOperator = type === 'single' ? 'is' : 'is between';

    // Update local state
    setValues(newValues_1);

    // Cancel in-flight debounced calls to prevent flicker/race conditions
    setFilterOperatorDebounced.cancel();
    setFilterValueDebounced.cancel();

    // Update global filter state atomically
    actions.setFilterOperator(column, newOperator);
    actions.setFilterValue(column, newValues_1);
  }, [values, column, actions, minMax]);
  return <Command data-sentry-element="Command" data-sentry-component="FilterValueNumberController" data-sentry-source-file="filter-value.tsx">
      <CommandList className="w-[300px] px-2 py-2" data-sentry-element="CommandList" data-sentry-source-file="filter-value.tsx">
        <CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx">
          <div className="flex w-full flex-col">
            <Tabs className={'flex flex-col'} onValueChange={v => changeType(v === 'range' ? 'range' : 'single')} value={isNumberRange ? 'range' : 'single'} data-sentry-element="Tabs" data-sentry-source-file="filter-value.tsx">
              <TabsList className="w-full *:text-xs" data-sentry-element="TabsList" data-sentry-source-file="filter-value.tsx">
                <TabsTrigger value="single" data-sentry-element="TabsTrigger" data-sentry-source-file="filter-value.tsx">{t('single', locale)}</TabsTrigger>
                <TabsTrigger value="range" data-sentry-element="TabsTrigger" data-sentry-source-file="filter-value.tsx">{t('range', locale)}</TabsTrigger>
              </TabsList>
              <TabsContent className="mt-4 flex flex-col gap-4" value="single" data-sentry-element="TabsContent" data-sentry-source-file="filter-value.tsx">
                {minMax && <Slider aria-orientation="horizontal" max={sliderMax} min={sliderMin} onValueChange={value_3 => changeNumber([...value_3])} step={1} value={[values[0]]} />}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{t('value', locale)}</span>
                  <DebouncedInput id="single" onChange={v_0 => changeNumber([Number(v_0)])} type="number" value={values[0].toString()} // Use values[0] directly
                data-sentry-element="DebouncedInput" data-sentry-source-file="filter-value.tsx" />
                  {column.unit && <span className="text-xs font-medium">{column.unit}</span>}
                </div>
              </TabsContent>
              <TabsContent className="mt-4 flex flex-col gap-4" value="range" data-sentry-element="TabsContent" data-sentry-source-file="filter-value.tsx">
                {minMax && <Slider aria-orientation="horizontal" max={sliderMax} min={sliderMin} onValueChange={value_4 => changeNumber([...value_4])} step={1} value={values} // Use values directly
              />}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium">{t('min', locale)}</span>
                    <DebouncedInput onChange={v_1 => changeMinNumber(Number(v_1))} type="number" value={values[0]} data-sentry-element="DebouncedInput" data-sentry-source-file="filter-value.tsx" />
                    {column.unit && <span className="text-xs font-medium">{column.unit}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium">{t('max', locale)}</span>
                    <DebouncedInput onChange={v_2 => changeMaxNumber(Number(v_2))} type="number" value={values[1]} data-sentry-element="DebouncedInput" data-sentry-source-file="filter-value.tsx" />
                    {column.unit && <span className="text-xs font-medium">{column.unit}</span>}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </CommandGroup>
      </CommandList>
    </Command>;
}
export function FilterValueOptionController<TData>({
  actions,
  column,
  filter,
  locale = 'en'
}: FilterValueControllerProps<TData, 'option'>) {
  const autoFocus = use(FilterEditorAutoFocus);
  // Compute initial options once per mount
  const initialOptions = useMemo(() => {
    const counts = column.getFacetedUniqueValues();
    return column.getOptions().map(o => ({
      ...o,
      count: counts?.get(o.value) ?? 0,
      initialSelected: filter?.values.includes(o.value) ?? false,
      selected: filter?.values.includes(o.value)
    }));
  }, []);

  // Selection follows the filter values; `initialSelected` keeps the grouping fixed per mount
  const options = initialOptions.map(o_0 => ({
    ...o_0,
    selected: filter?.values.includes(o_0.value)
  }));
  const {
    customNumber
  } = column;
  const custom = customNumberOption(column, filter?.values);
  const [customText, setCustomText] = useState(custom?.value ?? '');
  // Follow the filter when its custom number changes, from this field or elsewhere.
  const [shownCustomValue, setShownCustomValue] = useState(custom?.value);
  if (custom?.value !== shownCustomValue) {
    setShownCustomValue(custom?.value);
    setCustomText(custom?.value ?? '');
  }

  // Stable actions keep each debounce, and so its cancel, across renders.
  // Explicit arguments keep the generic action typed for this option column.
  const setFilterValueDebounced = useDebounceCallback<[Column<TData, 'option'>, string[]], void>(actions.setFilterValue, 500);
  const removeFilterDebounced = useDebounceCallback(actions.removeFilter, 500);
  const cancelCustomNumber = () => {
    setFilterValueDebounced.cancel();
    removeFilterDebounced.cancel();
  };
  const changeCustomText = (text: string) => {
    setCustomText(text);
    cancelCustomNumber();
    const option = customNumber?.toOption(text);
    if (option) setFilterValueDebounced(column, [option.value]);
    // A blank field drops an applied custom number; other invalid text sets nothing.
    else if (text === '' && custom) removeFilterDebounced(column.id);
  };
  const handleToggle = (value: string, checked: boolean) => {
    // The filter holds one value, so choosing an option drops a custom number still pending.
    cancelCustomNumber();
    setCustomText('');
    // For single-option filters, replace the entire selection instead of adding/removing
    if (checked) {
      actions.setFilterValue(column, [value]);
    } else {
      // Remove the entire filter when unchecking the only selected option
      actions.removeFilter(column.id);
    }
  };

  // Derive groups based on `initialSelected` only
  const {
    selectedOptions,
    unselectedOptions
  } = useMemo(() => {
    const sel: typeof options = [];
    const unsel: typeof options = [];
    for (const o_1 of options) {
      if (o_1.initialSelected) sel.push(o_1);else unsel.push(o_1);
    }
    return {
      selectedOptions: sel,
      unselectedOptions: unsel
    };
  }, [options]);
  return <Command loop data-sentry-element="Command" data-sentry-component="FilterValueOptionController" data-sentry-source-file="filter-value.tsx">
      {customNumber ? <label className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium">
          {customNumber.label}
          <Input autoFocus={autoFocus} className="h-8" min={0} onChange={event => changeCustomText(event.target.value)}
      // Enter would otherwise choose the highlighted option.
      onKeyDown={event_0 => {
        if (event_0.key === 'Enter') event_0.stopPropagation();
      }} type="number" value={customText} />
        </label> : <>
          <CommandInput autoFocus={autoFocus} placeholder={t('search', locale)} />
          <CommandEmpty>{t('noresults', locale)}</CommandEmpty>
        </>}
      <CommandList className="max-h-fit" data-sentry-element="CommandList" data-sentry-source-file="filter-value.tsx">
        <CommandGroup className={cn(selectedOptions.length === 0 && !custom && 'hidden')} data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx">
          {custom && <OptionItem onToggle={handleToggle} option={custom} />}
          {selectedOptions.map(option_0 => <OptionItem key={option_0.value} onToggle={handleToggle} option={option_0} />)}
        </CommandGroup>
        <CommandSeparator data-sentry-element="CommandSeparator" data-sentry-source-file="filter-value.tsx" />
        <CommandGroup className={cn(unselectedOptions.length === 0 && 'hidden')} data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx">
          {unselectedOptions.map(option_1 => <OptionItem key={option_1.value} onToggle={handleToggle} option={option_1} />)}
        </CommandGroup>
      </CommandList>
    </Command>;
}
export function FilterValueTextController(t0) {
  const $ = _c(10);
  const {
    actions,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const autoFocus = use(FilterEditorAutoFocus);
  let t2;
  if ($[0] !== actions || $[1] !== column) {
    t2 = value => {
      actions.setFilterValue(column, [String(value)]);
    };
    $[0] = actions;
    $[1] = column;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const changeText = t2;
  let t3;
  if ($[3] !== locale) {
    t3 = t("search", locale);
    $[3] = locale;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = filter?.values[0] ?? "";
  let t5;
  if ($[5] !== autoFocus || $[6] !== changeText || $[7] !== t3 || $[8] !== t4) {
    t5 = <Command data-sentry-element="Command" data-sentry-component="FilterValueTextController" data-sentry-source-file="filter-value.tsx"><CommandList className="max-h-fit" data-sentry-element="CommandList" data-sentry-source-file="filter-value.tsx"><CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx"><CommandItem data-sentry-element="CommandItem" data-sentry-source-file="filter-value.tsx"><DebouncedInput autoFocus={autoFocus} onChange={changeText} placeholder={t3} value={t4} data-sentry-element="DebouncedInput" data-sentry-source-file="filter-value.tsx" /></CommandItem></CommandGroup></CommandList></Command>;
    $[5] = autoFocus;
    $[6] = changeText;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function CountInput(t0) {
  const $ = _c(10);
  const {
    label,
    max,
    min,
    onChange,
    value
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = event => onChange(event.target.value);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== max || $[3] !== min || $[4] !== t1 || $[5] !== value) {
    t2 = <Input className="h-8" max={max} min={min} onChange={t1} step={1} type="number" value={value} data-sentry-element="Input" data-sentry-source-file="filter-value.tsx" />;
    $[2] = max;
    $[3] = min;
    $[4] = t1;
    $[5] = value;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== label || $[8] !== t2) {
    t3 = <label className="flex items-center gap-2 text-xs font-medium" data-sentry-component="CountInput" data-sentry-source-file="filter-value.tsx">{label}{t2}</label>;
    $[7] = label;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/*
 * Any number of currencies, or any currency, as the multi-option filter's checkbox list. The
 * trigger names one currency and counts several, so it stays narrow beside the amount inputs; its
 * accessible name lists them all. Currencies keep the menu's order, and ticking every currency is
 * any currency.
 */
function CurrencySelect(t0) {
  const $ = _c(89);
  const {
    className,
    currencies,
    locale,
    onChange,
    onClose,
    value,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "outline" : t1;
  const [open, setOpen] = useState(false);
  let T0;
  let T1;
  let T2;
  let chooseAny;
  let summary;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let toggle;
  if ($[0] !== currencies || $[1] !== locale || $[2] !== onChange || $[3] !== onClose || $[4] !== open || $[5] !== value) {
    let t13;
    if ($[23] !== currencies) {
      t13 = code => currencies.find(option => option.value === code)?.label ?? code;
      $[23] = currencies;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    const labels = value.map(t13);
    summary = labels.length > 1 ? t("currencyCount", locale).replace("{n}", String(labels.length)) : labels[0] ?? t("anyCurrencyShort", locale);
    let t14;
    if ($[25] !== currencies || $[26] !== onChange || $[27] !== value) {
      t14 = (currency, checked) => {
        const next = currencies.map(_temp11).filter(code_0 => code_0 === currency ? checked : value.includes(code_0));
        onChange(next.length === currencies.length ? [] : next);
      };
      $[25] = currencies;
      $[26] = onChange;
      $[27] = value;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    toggle = t14;
    let t15;
    if ($[29] !== onChange) {
      t15 = () => {
        onChange([]);
        setOpen(false);
      };
      $[29] = onChange;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    chooseAny = t15;
    T2 = Popover;
    t7 = setOpen;
    if ($[31] !== onClose) {
      t8 = isOpen => {
        if (!isOpen) {
          onClose?.();
        }
      };
      $[31] = onClose;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    t9 = open;
    t10 = "Popover";
    t11 = "CurrencySelect";
    t12 = "filter-value.tsx";
    T1 = PopoverTrigger;
    t4 = true;
    t5 = "PopoverTrigger";
    t6 = "filter-value.tsx";
    T0 = Button;
    if ($[33] !== locale) {
      t2 = t("currency", locale);
      $[33] = locale;
      $[34] = t2;
    } else {
      t2 = $[34];
    }
    t3 = labels.join(", ") || t("anyCurrencyShort", locale);
    $[0] = currencies;
    $[1] = locale;
    $[2] = onChange;
    $[3] = onClose;
    $[4] = open;
    $[5] = value;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = chooseAny;
    $[10] = summary;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = toggle;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    chooseAny = $[9];
    summary = $[10];
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
    toggle = $[22];
  }
  const t13 = `${t2}: ${t3}`;
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronDownIcon className="text-muted-foreground" data-sentry-element="ChevronDownIcon" data-sentry-source-file="filter-value.tsx" />;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== T0 || $[37] !== className || $[38] !== summary || $[39] !== t13 || $[40] !== variant) {
    t15 = <T0 aria-label={t13} className={className} variant={variant} data-sentry-element="Button" data-sentry-source-file="filter-value.tsx">{summary}{t14}</T0>;
    $[36] = T0;
    $[37] = className;
    $[38] = summary;
    $[39] = t13;
    $[40] = variant;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== T1 || $[43] !== t15 || $[44] !== t4 || $[45] !== t5 || $[46] !== t6) {
    t16 = <T1 asChild={t4} data-sentry-element={t5} data-sentry-source-file={t6}>{t15}</T1>;
    $[42] = T1;
    $[43] = t15;
    $[44] = t4;
    $[45] = t5;
    $[46] = t6;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== locale) {
    t17 = t("search", locale);
    $[48] = locale;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t17) {
    t18 = <CommandInput autoFocus={true} placeholder={t17} data-sentry-element="CommandInput" data-sentry-source-file="filter-value.tsx" />;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== locale) {
    t19 = t("noresults", locale);
    $[52] = locale;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== t19) {
    t20 = <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="filter-value.tsx">{t19}</CommandEmpty>;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  const t21 = value.length === 0;
  let t22;
  if ($[56] !== locale) {
    t22 = t("anyCurrency", locale);
    $[56] = locale;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== chooseAny || $[59] !== t21 || $[60] !== t22) {
    t23 = <CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx"><CommandItem data-checked={t21} onSelect={chooseAny} data-sentry-element="CommandItem" data-sentry-source-file="filter-value.tsx">{t22}</CommandItem></CommandGroup>;
    $[58] = chooseAny;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <CommandSeparator data-sentry-element="CommandSeparator" data-sentry-source-file="filter-value.tsx" />;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== currencies || $[64] !== toggle || $[65] !== value) {
    let t26;
    if ($[67] !== toggle || $[68] !== value) {
      t26 = option_1 => <OptionItem key={option_1.value} onToggle={toggle} option={{
        ...option_1,
        selected: value.includes(option_1.value)
      }} />;
      $[67] = toggle;
      $[68] = value;
      $[69] = t26;
    } else {
      t26 = $[69];
    }
    t25 = currencies.map(t26);
    $[63] = currencies;
    $[64] = toggle;
    $[65] = value;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[70] !== t25) {
    t26 = <CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="filter-value.tsx">{t25}</CommandGroup>;
    $[70] = t25;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t23 || $[73] !== t26) {
    t27 = <CommandList className="max-h-fit" data-sentry-element="CommandList" data-sentry-source-file="filter-value.tsx">{t23}{t24}{t26}</CommandList>;
    $[72] = t23;
    $[73] = t26;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== t18 || $[76] !== t20 || $[77] !== t27) {
    t28 = <PopoverContent align="end" className="w-fit p-0" data-sentry-element="PopoverContent" data-sentry-source-file="filter-value.tsx"><Command loop={true} data-sentry-element="Command" data-sentry-source-file="filter-value.tsx">{t18}{t20}{t27}</Command></PopoverContent>;
    $[75] = t18;
    $[76] = t20;
    $[77] = t27;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  let t29;
  if ($[79] !== T2 || $[80] !== t10 || $[81] !== t11 || $[82] !== t12 || $[83] !== t16 || $[84] !== t28 || $[85] !== t7 || $[86] !== t8 || $[87] !== t9) {
    t29 = <T2 onOpenChange={t7} onOpenChangeComplete={t8} open={t9} data-sentry-element={t10} data-sentry-component={t11} data-sentry-source-file={t12}>{t16}{t28}</T2>;
    $[79] = T2;
    $[80] = t10;
    $[81] = t11;
    $[82] = t12;
    $[83] = t16;
    $[84] = t28;
    $[85] = t7;
    $[86] = t8;
    $[87] = t9;
    $[88] = t29;
  } else {
    t29 = $[88];
  }
  return t29;
}

/**
 * The option a custom number in the filter stands for, keeping the stored value and marked
 * selected; undefined when the filter holds one of the column's options.
 */
function _temp11(option_0) {
  return option_0.value;
}
function customNumberOption<TData>(column: Column<TData, 'option'>, values: string[] = []) {
  // An option filter holds one value.
  const [value] = values;
  if (value === undefined || column.getOptions().some(option => option.value === value)) {
    return undefined;
  }
  const option = column.customNumber?.toOption(value);
  return option && {
    ...option,
    initialSelected: true,
    selected: true,
    value
  };
}

/**
 * A typed from-to of whole days from today, either end negative. It filters once both ends read
 * as numbers, after typing pauses; closing the popover meanwhile still applies it.
 */
function DayRangeInputs(t0) {
  const $ = _c(28);
  const {
    apply: applyValues,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  let t2;
  if ($[0] !== filter) {
    t2 = filter?.values.every(isDayOffset) ? filter.values : [];
    $[0] = filter;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const current = t2;
  const [fromText, setFromText] = useState(String(current[0]?.days ?? ""));
  let t3;
  if ($[2] !== current) {
    t3 = current.at(-1)?.days ?? "";
    $[2] = current;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [toText, setToText] = useState(String(t3));
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = values => {
      setFromText(String(values[0].days));
      setToText(String(values.at(-1)?.days));
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const apply = usePendingApply(applyValues, t4);
  let t5;
  if ($[5] !== apply) {
    t5 = function change(nextFromText, nextToText) {
      setFromText(nextFromText);
      setToText(nextToText);
      const from = parseDayOffset(nextFromText);
      const to = parseDayOffset(nextToText);
      if (from === undefined || to === undefined) {
        apply.cancel();
        return;
      }
      const [start, end] = from <= to ? [from, to] : [to, from];
      apply(start === end ? [{
        days: start
      }] : [{
        days: start
      }, {
        days: end
      }]);
    };
    $[5] = apply;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const change = t5;
  let t6;
  if ($[7] !== locale) {
    t6 = t("from", locale);
    $[7] = locale;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== change || $[10] !== toText) {
    t7 = text => change(text, toText);
    $[9] = change;
    $[10] = toText;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== fromText || $[13] !== t6 || $[14] !== t7) {
    t8 = <CountInput label={t6} max={MAX_DAY_OFFSET} min={-MAX_DAY_OFFSET} onChange={t7} value={fromText} data-sentry-element="CountInput" data-sentry-source-file="filter-value.tsx" />;
    $[12] = fromText;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== locale) {
    t9 = t("to", locale);
    $[16] = locale;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== change || $[19] !== fromText) {
    t10 = text_0 => change(fromText, text_0);
    $[18] = change;
    $[19] = fromText;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9 || $[23] !== toText) {
    t11 = <CountInput label={t9} max={MAX_DAY_OFFSET} min={-MAX_DAY_OFFSET} onChange={t10} value={toText} data-sentry-element="CountInput" data-sentry-source-file="filter-value.tsx" />;
    $[21] = t10;
    $[22] = t9;
    $[23] = toText;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t8) {
    t12 = <CommandGroup data-sentry-element="CommandGroup" data-sentry-component="DayRangeInputs" data-sentry-source-file="filter-value.tsx"><div className="grid grid-cols-2 gap-4 px-2 py-1.5">{t8}{t11}</div></CommandGroup>;
    $[25] = t11;
    $[26] = t8;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}

/*
 * One pending update for a set of inputs' lifetime. Leaving them (closing the popover, switching
 * tab) applies it at once, so a choice made afterwards, or removing the filter, wins.
 */
function usePendingApply(applyValues, show) {
  const $ = _c(6);
  let t0;
  if ($[0] !== applyValues || $[1] !== show) {
    t0 = () => debounce(values => {
      show?.(values);
      applyValues(values);
    }, 500);
    $[0] = applyValues;
    $[1] = show;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [pending] = useState(t0);
  let t1;
  let t2;
  if ($[3] !== pending) {
    t1 = () => () => pending.flush();
    t2 = [pending];
    $[3] = pending;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return pending;
}
const WINDOW_UNITS = {
  days: {
    after: 'daysAfter',
    before: 'daysBefore',
    max: MAX_DAY_OFFSET
  },
  months: {
    after: 'monthsAfter',
    before: 'monthsBefore',
    max: MAX_MONTH_OFFSET
  }
} as const;

/* The amounts a filter opens with: Min and Max on the Range tab, one amount on Single. */
function initialAmounts(filter: FilterModel<'amount'> | undefined): Array<null | number> {
  if (!filter) return [null];
  const [first = null, second = null] = filter.values.map(value => value.amount);
  if (filter.operator === 'is between') return [first, second];
  return RANGE_TAB_OPERATORS.has(filter.operator) ? rangeAmounts(filter.operator, first) : [first];
}

/* Last, this and next month, each covering the whole calendar month. */
function MonthChoices(t0) {
  const $ = _c(8);
  const {
    current,
    locale: t1,
    onChoose
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  let t2;
  if ($[0] !== current) {
    t2 = findMonthChoice(current);
    $[0] = current;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const chosen = t2;
  let t3;
  if ($[2] !== chosen || $[3] !== locale || $[4] !== onChoose) {
    t3 = MONTH_CHOICES.map(choice => {
      const label = t(choice.label, locale);
      return <CommandItem className="flex items-center gap-1.5" key={label} onSelect={() => onChoose(monthChoice(choice.months))} value={label}><Checkbox checked={chosen === choice} /><span>{label}</span></CommandItem>;
    });
    $[2] = chosen;
    $[3] = locale;
    $[4] = onChoose;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <CommandGroup data-sentry-element="CommandGroup" data-sentry-component="MonthChoices" data-sentry-source-file="filter-value.tsx">{t3}</CommandGroup>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

/* A lone amount on the Range tab: Max under a "less than" operator, Min otherwise. */
function rangeAmounts(operator: FilterOperators['amount'] | undefined, amount: null | number) {
  return operator === 'is less than' || operator === 'is less than or equal to' ? [null, amount] : [amount, null];
}
function RelativeDateChoices(t0) {
  const $ = _c(23);
  const {
    filter,
    locale: t1,
    onChoose,
    onChooseOverdue,
    pastDue
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  let current;
  let t2;
  if ($[0] !== filter) {
    current = filter?.values.every(isDayOffset) ? filter.values : [];
    t2 = Math.max(0, ...current.map(_temp12)) || 7;
    $[0] = filter;
    $[1] = current;
    $[2] = t2;
  } else {
    current = $[1];
    t2 = $[2];
  }
  const initialDays = t2;
  const [daysText, setDaysText] = useState(String(initialDays));
  let t3;
  if ($[3] !== current || $[4] !== daysText || $[5] !== locale || $[6] !== onChoose || $[7] !== onChooseOverdue || $[8] !== pastDue) {
    const days = parseRelativeDays(daysText) ?? 1;
    let t4;
    if ($[10] !== locale) {
      t4 = t("days", locale);
      $[10] = locale;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = event => setDaysText(event.target.value);
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== daysText) {
      t6 = <Input className="h-8" max={MAX_DAY_OFFSET} min={1} onChange={t5} type="number" value={daysText} data-sentry-element="Input" data-sentry-source-file="filter-value.tsx" />;
      $[13] = daysText;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t4 || $[16] !== t6) {
      t7 = <label className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium">{t4}{t6}</label>;
      $[15] = t4;
      $[16] = t6;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== current || $[19] !== locale || $[20] !== onChoose || $[21] !== pastDue) {
      t8 = values => {
        const label_0 = relativeDateLabel(values, locale);
        const checked = pastDue === undefined && JSON.stringify(values) === JSON.stringify(current);
        return <CommandItem className="flex items-center gap-1.5" key={label_0} onSelect={() => onChoose(values)} value={label_0}><Checkbox checked={checked} /><span>{label_0}</span></CommandItem>;
      };
      $[18] = current;
      $[19] = locale;
      $[20] = onChoose;
      $[21] = pastDue;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    t3 = <CommandGroup data-sentry-element="CommandGroup" data-sentry-component="RelativeDateChoices" data-sentry-source-file="filter-value.tsx">{t7}{onChooseOverdue ? [0, days].map(overdue => {
        const label = overdueLabel(overdue, locale);
        return <CommandItem className="flex items-center gap-1.5" key={label} onSelect={() => onChooseOverdue(overdue)} value={label}><Checkbox checked={pastDue === overdue} /><span>{label}</span></CommandItem>;
      }) : null}{relativeDateChoices(days).map(t8)}</CommandGroup>;
    $[3] = current;
    $[4] = daysText;
    $[5] = locale;
    $[6] = onChoose;
    $[7] = onChooseOverdue;
    $[8] = pastDue;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
/**
 * How many days or months before and after today a window reaches. It filters once both read as
 * whole numbers, after typing pauses; closing the popover meanwhile still applies it.
 */
function _temp12(value) {
  return Math.abs(value.days);
}
function WindowInputs(t0) {
  const $ = _c(40);
  const {
    apply: applyValues,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  let t2;
  if ($[0] !== filter) {
    t2 = filter?.values.every(isRelativeDate) ? readWindow(filter.values) : undefined;
    $[0] = filter;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initial = t2;
  const [unit, setUnit] = useState(initial?.unit ?? "days");
  const [beforeText, setBeforeText] = useState(initial ? String(initial.before) : "");
  const [afterText, setAfterText] = useState(initial ? String(initial.after) : "");
  const apply = usePendingApply(applyValues);
  const labels = WINDOW_UNITS[unit];
  let t3;
  if ($[2] !== apply) {
    t3 = function change(nextBeforeText, nextAfterText, nextUnit) {
      setBeforeText(nextBeforeText);
      setAfterText(nextAfterText);
      setUnit(nextUnit);
      const before = parseWindowCount(nextBeforeText, nextUnit);
      const after = parseWindowCount(nextAfterText, nextUnit);
      if (before === undefined || after === undefined) {
        apply.cancel();
        return;
      }
      apply(windowAroundToday({
        after,
        before,
        unit: nextUnit
      }));
    };
    $[2] = apply;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const change = t3;
  let t4;
  if ($[4] !== afterText || $[5] !== beforeText || $[6] !== change || $[7] !== locale || $[8] !== unit) {
    t4 = <ButtonGroup className="px-2 py-1.5" data-sentry-element="ButtonGroup" data-sentry-source-file="filter-value.tsx">{(["days", "months"] as const).map(option => <Button aria-pressed={unit === option} key={option} onClick={() => change(beforeText, afterText, option)} size="xs" variant={unit === option ? "secondary" : "outline"}>{t(option, locale)}</Button>)}</ButtonGroup>;
    $[4] = afterText;
    $[5] = beforeText;
    $[6] = change;
    $[7] = locale;
    $[8] = unit;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== labels.before || $[11] !== locale) {
    t5 = t(labels.before, locale);
    $[10] = labels.before;
    $[11] = locale;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== afterText || $[14] !== change || $[15] !== unit) {
    t6 = text => change(text, afterText, unit);
    $[13] = afterText;
    $[14] = change;
    $[15] = unit;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== beforeText || $[18] !== labels.max || $[19] !== t5 || $[20] !== t6) {
    t7 = <CountInput label={t5} max={labels.max} min={0} onChange={t6} value={beforeText} data-sentry-element="CountInput" data-sentry-source-file="filter-value.tsx" />;
    $[17] = beforeText;
    $[18] = labels.max;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== labels.after || $[23] !== locale) {
    t8 = t(labels.after, locale);
    $[22] = labels.after;
    $[23] = locale;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== beforeText || $[26] !== change || $[27] !== unit) {
    t9 = text_0 => change(beforeText, text_0, unit);
    $[25] = beforeText;
    $[26] = change;
    $[27] = unit;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== afterText || $[30] !== labels.max || $[31] !== t8 || $[32] !== t9) {
    t10 = <CountInput label={t8} max={labels.max} min={0} onChange={t9} value={afterText} data-sentry-element="CountInput" data-sentry-source-file="filter-value.tsx" />;
    $[29] = afterText;
    $[30] = labels.max;
    $[31] = t8;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t10 || $[35] !== t7) {
    t11 = <div className="grid grid-cols-2 gap-4 px-2 py-1.5">{t7}{t10}</div>;
    $[34] = t10;
    $[35] = t7;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t11 || $[38] !== t4) {
    t12 = <CommandGroup data-sentry-element="CommandGroup" data-sentry-component="WindowInputs" data-sentry-source-file="filter-value.tsx">{t4}{t11}</CommandGroup>;
    $[37] = t11;
    $[38] = t4;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
}